import React from "react";
import { useNavigate } from "react-router-dom";
import { Download, Printer, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  BackLink,
  HeroStatus,
  Section,
  SectionLabel,
  EntityCard,
  Card,
  CardTitle,
  KvGrid,
  KvItem,
  StatCard,
  StatsRow,
  Tabs,
  TabPanel,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  fmtItalianDateTime,
  fmtItalianDate,
  orDash,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

/* ============================================================
 * Defensive accessors per shape company OpenAPI.it
 * (data può essere array singolo o oggetto)
 * ============================================================ */

const pickCompany = (rd) => {
  if (!rd) return {};
  const arr = Array.isArray(rd?.data) ? rd.data : null;
  const c = (arr && arr[0]) || rd?.data || rd;
  return c && typeof c === "object" ? c : {};
};

const pickRegisteredOffice = (c) =>
  c?.address?.registeredOffice || c?.address || c?.sedeLegale || c?.sede_legale || {};

const pickContacts = (c) => c?.contacts || c?.contatti || {};

const pickAteco = (c) =>
  c?.atecoClassification || c?.ateco || c?.classificazioneAteco || {};

const pickFinancials = (c) =>
  c?.financials || c?.balanceSheet || c?.bilancio || c?.dati_economici || null;

/* ============================================================
 * Number formatters - italian
 * ============================================================ */

const fmtEuroShort = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return EM_DASH;
  const v = Number(n);
  if (v >= 1_000_000) return `€ ${(v / 1_000_000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `€ ${(v / 1_000).toLocaleString("it-IT", { maximumFractionDigits: 0 })}K`;
  return `€ ${v.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
};

const trendArrow = (pct) => {
  if (pct === null || pct === undefined || isNaN(Number(pct))) return null;
  const v = Number(pct);
  if (v > 0) return { label: `↑ +${v}%`, color: "success" };
  if (v < 0) return { label: `↓ ${v}%`, color: "danger" };
  return { label: `± 0%`, color: "default" };
};

/* ============================================================
 * ResultCompany
 * ============================================================ */

export const ResultCompany = ({ request, onNewSearch }) => {
  const navigate = useNavigate();
  const isAdvanced = request?.service_id === "company_it_advanced";
  const completedAt = request?.completed_at || request?.created_at;

  const c = pickCompany(request?.response_data);
  const ro = pickRegisteredOffice(c);
  const contacts = pickContacts(c);
  const ateco = pickAteco(c);
  const fin = pickFinancials(c);

  const name = c?.companyName || c?.denominazione || c?.ragione_sociale || EM_DASH;
  const piva = c?.vatCode || c?.piva || c?.partita_iva;
  const cf = c?.taxCode || c?.cf || c?.codice_fiscale;
  const legalForm = c?.legalForm || c?.formaGiuridica || c?.forma_giuridica;
  const status = c?.status || c?.stato;

  const metaParts = [];
  if (piva) metaParts.push(`P.IVA ${upperOrDash(piva)}`);
  if (cf && cf !== piva) metaParts.push(`C.F. ${upperOrDash(cf)}`);

  const subParts = [];
  if (legalForm) subParts.push(legalForm);
  if (status) subParts.push(status);

  // Tab visibility logic (defensive)
  const tabs = [
    { id: "identificazione", label: "Identificazione" },
    {
      id: "sede",
      label: "Sede e contatti",
      hidden: !ro && Object.keys(contacts).length === 0,
    },
    { id: "attivita", label: "Attività", hidden: Object.keys(ateco).length === 0 },
    { id: "economici", label: "Dati economici", hidden: !isAdvanced || !fin },
  ];

  const onExportJson = () => {
    try {
      const blob = new Blob([JSON.stringify(request, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `company_${request?.id || "result"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore export JSON"); }
  };

  const onPrint = () => window.print();
  const handleNewSearch = () => (onNewSearch ? onNewSearch() : navigate("/search/company"));

  const addrFull = [ro?.street || ro?.indirizzo, ro?.zipCode || ro?.cap, ro?.city || ro?.comune, ro?.province || ro?.provincia, ro?.region || ro?.regione]
    .filter(Boolean);
  const addrFullStr = addrFull.length > 0
    ? `${ro?.street || ro?.indirizzo || ""}${ro?.zipCode || ro?.cap ? `, ${ro?.zipCode || ro?.cap}` : ""}${ro?.city || ro?.comune ? ` ${ro?.city || ro?.comune}` : ""}${ro?.province || ro?.provincia ? ` (${ro?.province || ro?.provincia})` : ""}${ro?.region ? `, ${ro?.region}` : ""}`.trim()
    : null;

  const trend = trendArrow(fin?.profitTrendPercent);

  return (
    <div className="max-w-3xl" data-testid="result-company">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="success"
        title="Dati azienda recuperati"
        description={`Fonte: Registro Imprese · ${fmtItalianDateTime(completedAt)}`}
        testId="result-hero"
      />

      <Section>
        <EntityCard
          title={null}
          entity={String(name).toUpperCase()}
          meta={metaParts.join(" · ")}
          sub={subParts.join(" · ")}
        />
      </Section>

      <Tabs defaultTab="identificazione" tabs={tabs}>
        <TabPanel id="identificazione">
          <Card>
            <CardTitle>Dati identificativi</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Ragione sociale" value={orDash(name)} testId="kv-name" />
              <KvItem label="Forma giuridica" value={orDash(legalForm)} testId="kv-form" />
              <KvItem label="P.IVA" value={upperOrDash(piva)} mono testId="kv-piva" />
              <KvItem label="Codice Fiscale" value={upperOrDash(cf)} mono testId="kv-cf" />
              <KvItem label="Stato" value={orDash(status)} testId="kv-status" />
              <KvItem label="Data costituzione" value={fmtItalianDate(c?.startDate || c?.dataCostituzione)} testId="kv-start" />
            </KvGrid>
          </Card>
        </TabPanel>

        <TabPanel id="sede">
          <Section>
            <Card>
              <CardTitle>Sede legale</CardTitle>
              <KvGrid columns={2}>
                <KvItem label="Indirizzo" value={addrFullStr || EM_DASH} columnSpan />
                <KvItem label="Comune" value={orDash(ro?.city || ro?.comune)} />
                <KvItem label="Provincia" value={orDash(ro?.province || ro?.provincia)} />
                <KvItem label="CAP" value={orDash(ro?.zipCode || ro?.cap)} mono />
                <KvItem label="Regione" value={orDash(ro?.region || ro?.regione)} />
              </KvGrid>
            </Card>
          </Section>
          {Object.keys(contacts).length > 0 && (
            <Section>
              <Card>
                <CardTitle>Contatti</CardTitle>
                <KvGrid columns={2}>
                  <KvItem label="PEC" value={orDash(contacts?.pec)} mono link={contacts?.pec ? `mailto:${contacts.pec}` : null} />
                  <KvItem label="Telefono" value={orDash(contacts?.phone || contacts?.telefono)} link={contacts?.phone ? `tel:${contacts.phone}` : null} />
                  <KvItem label="Email" value={orDash(contacts?.email)} link={contacts?.email ? `mailto:${contacts.email}` : null} />
                  <KvItem label="Sito web" value={orDash(contacts?.website || contacts?.sito_web)} link={contacts?.website ? (contacts.website.startsWith("http") ? contacts.website : `https://${contacts.website}`) : null} />
                </KvGrid>
              </Card>
            </Section>
          )}
        </TabPanel>

        <TabPanel id="attivita">
          <Card>
            <CardTitle>Classificazione attività</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="Codice ATECO" value={orDash(ateco?.code || ateco?.codice)} mono />
              <KvItem label="Settore" value={orDash(ateco?.sector || ateco?.settore)} />
              <KvItem label="Descrizione attività" value={orDash(ateco?.description || ateco?.descrizione)} columnSpan />
            </KvGrid>
          </Card>
        </TabPanel>

        <TabPanel id="economici">
          <SectionLabel>
            Dati economici{fin?.year ? ` (esercizio ${fin.year})` : " (ultimo esercizio)"}
          </SectionLabel>
          <StatsRow columns={4}>
            <StatCard label="Ricavi" value={fmtEuroShort(fin?.revenue)} detail={fin?.year ? `Esercizio ${fin.year}` : null} testId="stat-revenue" />
            <StatCard
              label="Utile"
              value={fmtEuroShort(fin?.profit)}
              detail={trend ? trend.label : null}
              detailColor={trend ? trend.color : "default"}
              testId="stat-profit"
            />
            <StatCard label="Equity" value={fmtEuroShort(fin?.equity)} detail="Capitale netto" testId="stat-equity" />
            <StatCard label="Dipendenti" value={fin?.employees ?? EM_DASH} detail={fin?.year ? `Media ${fin.year}` : null} testId="stat-employees" />
          </StatsRow>
        </TabPanel>
      </Tabs>

      <FooterActions>
        <ActionButton variant="primary" icon={Plus} onClick={handleNewSearch} testId="action-new">
          Nuova ricerca
        </ActionButton>
        <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
          Esporta JSON
        </ActionButton>
        <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
          Stampa
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};
